import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";

function extractPagedListPayload(response) {
    if (!response || typeof response !== "object") {
        return { list: [], totalElements: 0, totalPages: 0, page: 0, pageSize: 0 };
    }
    const inner =
        response.data != null && typeof response.data === "object" && !Array.isArray(response.data)
            ? response.data
            : null;
    const pageBlock =
        inner != null &&
        (Array.isArray(inner.content) ||
            inner.totalElements != null ||
            inner.total_elements != null ||
            inner.totalPages != null ||
            inner.total_pages != null ||
            inner.page != null)
            ? inner
            : response;
    const content =
        pageBlock?.content ?? response?.content ?? inner?.content ?? [];
    const list = Array.isArray(content) ? content : [];
    const pageMeta =
        response.page ??
        response?.data?.page ??
        pageBlock?.page ??
        inner?.page ??
        null;
    const toInt = (v) => {
        if (v == null) return 0;
        const n = typeof v === "string" ? parseInt(v, 10) : Number(v);
        return Number.isFinite(n) ? n : 0;
    };
    const totalElements = toInt(
        pageMeta?.totalElements ??
            pageMeta?.total_elements ??
            pageBlock?.totalElements ??
            pageBlock?.total_elements ??
            response?.totalElements ??
            list.length,
    );
    const totalPages = toInt(
        pageMeta?.totalPages ??
            pageMeta?.total_pages ??
            pageBlock?.totalPages ??
            pageBlock?.total_pages ??
            response?.totalPages,
    );
    const page = toInt(pageMeta?.number ?? pageBlock?.number ?? response?.number);
    const pageSize = toInt(pageMeta?.size ?? pageBlock?.size ?? response?.size);
    return { list, totalElements, totalPages, page, pageSize };
}

function failureFromResponse(response, fallback) {
    if (response && (response.code === "FAILURE" || response.code === "NO_DATA")) {
        return {
            success: false,
            error: response.message || fallback,
            code: response.code,
            data: null,
        };
    }
    return null;
}

function errorFromCaught(error, fallback) {
    const response = error?.response;
    if (response && (response.code === "FAILURE" || response.code === "NO_DATA")) {
        return response.message || fallback;
    }
    if (response?.message) {
        return response.message;
    }
    if (error?.code === "FAILURE" && error?.message) {
        return error.message;
    }
    return error?.message || fallback;
}

const AdResourceService = {
    list: async (page = 0, pageSize = 20, sort = "createdAt:desc", filters = {}) => {
        try {
            const params = { page, pageSize, sort };
            const slidingId = typeof filters.slidingId === "string" ? filters.slidingId.trim() : "";
            const resourceType =
                typeof filters.resourceType === "string" ? filters.resourceType.trim() : "";
            if (slidingId) params.slidingId = slidingId;
            if (resourceType) params.resourceType = resourceType;

            const response = await ApiService.getData("/v1/ad", params);
            const failure = failureFromResponse(response, "Failed to list ad resources");
            if (failure) return failure;

            const { list, totalElements, totalPages, page: pageNumber, pageSize: size } =
                extractPagedListPayload(response);
            return {
                success: true,
                data: {
                    content: list,
                    totalElements,
                    totalPages,
                    page: pageNumber,
                    pageSize: size,
                },
            };
        } catch (error) {
            Logger.error("AdResourceService.list:", error);
            return { success: false, error: error.message || "Failed to list ad resources", data: null };
        }
    },

    getById: async (id) => {
        try {
            const trimmed = typeof id === "string" ? id.trim() : "";
            if (!trimmed) return { success: false, error: "Ad resource id required", data: null };

            const response = await ApiService.getData(`/v1/ad/${encodeURIComponent(trimmed)}`);
            const failure = failureFromResponse(response, "Ad resource not found");
            if (failure) return failure;

            return { success: true, data: response ?? null };
        } catch (error) {
            Logger.error("AdResourceService.getById:", error);
            return { success: false, error: error.message || "Failed to load ad resource", data: null };
        }
    },

    update: async (id, body) => {
        try {
            const trimmed = typeof id === "string" ? id.trim() : "";
            if (!trimmed) return { success: false, error: "Ad resource id required", data: null };

            const response = await ApiService.putData(
                `/v1/ad/${encodeURIComponent(trimmed)}`,
                body ?? {},
            );
            const failure = failureFromResponse(response, "Failed to update ad resource");
            if (failure) return failure;
            return { success: true, data: response ?? null };
        } catch (error) {
            Logger.error("AdResourceService.update:", error);
            return { success: false, error: error.message || "Failed to update ad resource", data: null };
        }
    },

    delete: async (id) => {
        try {
            const trimmed = typeof id === "string" ? id.trim() : "";
            if (!trimmed) return { success: false, error: "Ad resource id required", data: null };

            const response = await ApiService.deleteData(`/v1/ad/${encodeURIComponent(trimmed)}`);
            const failure = failureFromResponse(response, "Failed to delete ad resource");
            if (failure) return failure;
            return { success: true, data: response ?? null };
        } catch (error) {
            Logger.error("AdResourceService.delete:", error);
            return {
                success: false,
                error: errorFromCaught(error, "Failed to delete ad resource"),
                data: null,
            };
        }
    },

    generate: async ({ resourceType, prompt, referenceImage, strength, storyId } = {}) => {
        try {
            const type = typeof resourceType === "string" ? resourceType.trim() : "";
            if (!type) {
                return { success: false, error: "resourceType is required", data: null };
            }

            const promptText = typeof prompt === "string" ? prompt.trim() : "";
            const storyIdText = typeof storyId === "string" ? storyId.trim() : "";
            const isStoryAnimation = type === "STORY_ANIMATION";

            if (isStoryAnimation) {
                if (!storyIdText) {
                    return { success: false, error: "storyId is required", data: null };
                }
            } else if (!promptText) {
                return { success: false, error: "prompt is required", data: null };
            }

            const body = { resourceType: type };
            if (promptText) body.prompt = promptText;
            if (storyIdText) body.storyId = storyIdText;
            if (typeof referenceImage === "string" && referenceImage.trim()) {
                body.referenceImage = referenceImage.trim();
            }
            if (strength != null && strength !== "") {
                const strengthValue = Number(strength);
                if (Number.isFinite(strengthValue)) {
                    body.strength = strengthValue;
                }
            }

            const response = await ApiService.postData("/v1/ad/generate", body);
            const failure = failureFromResponse(response, "Failed to generate ad media");
            if (failure) return failure;
            return { success: true, data: response ?? null };
        } catch (error) {
            Logger.error("AdResourceService.generate:", error);
            return {
                success: false,
                error: error.message || "Failed to generate ad media",
                data: null,
            };
        }
    },

    /**
     * Crop a standalone story image to 1290×2796 JPEG and save as IPHONE_BACKGROUND.
     * cropX/cropY/cropWidth/cropHeight are 0–1 fractions of the source image.
     */
    createFromStoryCrop: async ({ storyId, cropX, cropY, cropWidth, cropHeight } = {}) => {
        try {
            const id = typeof storyId === "string" ? storyId.trim() : "";
            if (!id) {
                return { success: false, error: "storyId is required", data: null };
            }
            const body = { storyId: id };
            const fractions = [cropX, cropY, cropWidth, cropHeight];
            const present = fractions.filter((v) => v != null && v !== "").length;
            if (present === 4) {
                body.cropX = Number(cropX);
                body.cropY = Number(cropY);
                body.cropWidth = Number(cropWidth);
                body.cropHeight = Number(cropHeight);
            } else if (present !== 0) {
                return {
                    success: false,
                    error: "cropX, cropY, cropWidth, and cropHeight must all be set together",
                    data: null,
                };
            }

            const response = await ApiService.postData("/v1/ad/from-story-crop", body);
            const failure = failureFromResponse(response, "Failed to crop iPhone background");
            if (failure) return failure;
            return { success: true, data: response ?? null };
        } catch (error) {
            Logger.error("AdResourceService.createFromStoryCrop:", error);
            return {
                success: false,
                error: errorFromCaught(error, "Failed to crop iPhone background"),
                data: null,
            };
        }
    },

    /**
     * Crop a standalone story image to 1920×1080 JPEG and save as PC_BACKGROUND.
     * cropX/cropY/cropWidth/cropHeight are 0–1 fractions of the source image.
     */
    createPcBackgroundFromStoryCrop: async ({ storyId, cropX, cropY, cropWidth, cropHeight } = {}) => {
        try {
            const id = typeof storyId === "string" ? storyId.trim() : "";
            if (!id) {
                return { success: false, error: "storyId is required", data: null };
            }
            const body = { storyId: id };
            const fractions = [cropX, cropY, cropWidth, cropHeight];
            const present = fractions.filter((v) => v != null && v !== "").length;
            if (present === 4) {
                body.cropX = Number(cropX);
                body.cropY = Number(cropY);
                body.cropWidth = Number(cropWidth);
                body.cropHeight = Number(cropHeight);
            } else if (present !== 0) {
                return {
                    success: false,
                    error: "cropX, cropY, cropWidth, and cropHeight must all be set together",
                    data: null,
                };
            }

            const response = await ApiService.postData("/v1/ad/pc-background/from-story-crop", body);
            const failure = failureFromResponse(response, "Failed to crop PC background");
            if (failure) return failure;
            return { success: true, data: response ?? null };
        } catch (error) {
            Logger.error("AdResourceService.createPcBackgroundFromStoryCrop:", error);
            return {
                success: false,
                error: errorFromCaught(error, "Failed to crop PC background"),
                data: null,
            };
        }
    },

    listIphoneBackgroundStoryIds: async () => {
        try {
            const response = await ApiService.getData("/v1/ad/iphone-background/story-ids");
            const failure = failureFromResponse(response, "Failed to load iPhone background story ids");
            if (failure) return failure;
            const raw = response?.storyIds ?? response?.data?.storyIds ?? [];
            const storyIds = Array.isArray(raw)
                ? raw.map((id) => String(id)).filter(Boolean)
                : [];
            return { success: true, data: storyIds };
        } catch (error) {
            Logger.error("AdResourceService.listIphoneBackgroundStoryIds:", error);
            return {
                success: false,
                error: errorFromCaught(error, "Failed to load iPhone background story ids"),
                data: [],
            };
        }
    },
};

export default AdResourceService;
